import { HttpClient } from '@adapters/HttpClient';
import { Product, ProductFilters, ProductsApiResponse } from '../models/Product';

export class ProductApiAdapter {
  constructor(private httpClient: HttpClient) {}

  // 🎯 Obtener todos los productos
  async getProducts(filters: ProductFilters = {}): Promise<Product[]> {
    // Si el límite es muy alto (>= 1000), cargar todas las páginas automáticamente
    const limit = filters.limit ?? 10;
    if (limit >= 1000) {
      const allProducts: Product[] = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const pageResponse = await this.getProductsPaginated({
          ...filters,
          page: currentPage,
          limit: 1000, // Usar 1000 por página para evitar problemas con límites muy altos
        });

        allProducts.push(...pageResponse.products);
        hasMore = pageResponse.hasMore && pageResponse.products.length > 0;
        currentPage++;

        // Protección: evitar bucles infinitos
        if (currentPage > 1000) {
          console.warn('Límite de páginas alcanzado al cargar productos');
          break;
        }
      }

      return allProducts;
    }

    // Para límites menores, solo devolver la primera página
    const pageResponse = await this.getProductsPaginated(filters);
    return pageResponse.products;
  }

  // 🎯 Obtener productos paginados (para infinite scroll)
  async getProductsPaginated(filters: ProductFilters = {}) {
    const { searchTerm = '', page = 1, limit = 10, tenantChildId, includeTeam = false, fields } = filters;
    const useBasicFields = fields === 'basic';

    // Construir params base
    const params: any = {
      order_by: 'id',
      order_direction: 'asc',
      page,
      per_page: limit,
    };

    if (useBasicFields) {
      params.fields = 'basic';
    }

    // 🎯 Incluir relación team si se solicita (para mostrar nombre del cliente en 3PL)
    if (includeTeam && !useBasicFields) {
      params['with[]'] = 'team';
    }

    // 🎯 Agregar parámetro search si hay searchTerm
    if (searchTerm && searchTerm.trim().length > 0) {
      params['search'] = searchTerm.trim();
    }

    // 🎯 Agregar filtro de team_id si hay tenantChildId usando advanced_filters
    if (tenantChildId) {
      params['advanced_filters[filters][0][column]'] = 'team_id';
      params['advanced_filters[filters][0][operator]'] = 'in';
      params['advanced_filters[filters][0][value][0]'] = String(tenantChildId);
      params['advanced_filters[logic]'] = 'AND';
    }

    try {
      const response = await this.httpClient.get<ProductsApiResponse>('/products', params);
      const nextPage =
        response.meta && response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined;
      const total = response.meta?.total ?? response.data?.length ?? 0;
      return {
        products: response.data || [],
        nextPage,
        hasMore: !!nextPage,
        total,
      };
    } catch (error) {
      console.error('Error obteniendo productos paginados:', error);
      throw new Error('No se pudieron cargar los productos');
    }
  }

  // 🎯 Obtener un producto por ID
  async getProductById(id: number): Promise<Product> {
    try {
      const response = await this.httpClient.get<Product>(`/products/${id}`);
      return response;
    } catch (error) {
      console.error(`Error obteniendo producto ${id}:`, error);
      throw new Error(`No se pudo cargar el producto con ID ${id}`);
    }
  }
}
